import { assertNever, type Field as FieldType } from '@certhon/models';
import { CheckpointField } from './CheckpointField';
import { GroupField } from './GroupField';
import { NumericField } from './NumericField';
import { TaskField } from './TaskField';

interface FieldProps {
  field: FieldType;
  /** Index within the parent group, used for alternating row backgrounds. */
  index?: number;
  activeFieldId?: string;
  onSelectField?: (fieldId: string) => void;
}

/**
 * Type dispatch component. Renders the appropriate component for each field type
 * with exhaustiveness checking via assertNever.
 */
export function Field({
  field,
  index = 0,
  activeFieldId,
  onSelectField,
}: FieldProps) {
  const isActive = field.id === activeFieldId;
  const isEven = index % 2 === 0;

  switch (field.type) {
    case 'ROOT_FIELD':
      // ROOT_FIELD should be handled by the tree renderer, not by Field dispatch.
      // Render its children directly.
      return (
        <>
          {field.children.map((child, i) => (
            <Field
              key={child.id}
              field={child}
              index={i}
              activeFieldId={activeFieldId}
              onSelectField={onSelectField}
            />
          ))}
        </>
      );
    case 'GROUP_FIELD':
      return (
        <GroupField
          field={field}
          activeFieldId={activeFieldId}
          onSelectField={onSelectField}
        />
      );
    case 'CHECKPOINT_FIELD':
      return (
        <CheckpointField
          field={field}
          isActive={isActive}
          isEven={isEven}
          onSelect={onSelectField}
        />
      );
    case 'NUMERIC_FIELD':
      return (
        <NumericField
          field={field}
          isActive={isActive}
          isEven={isEven}
          onSelect={onSelectField}
        />
      );
    case 'TASK_FIELD':
      return (
        <TaskField
          field={field}
          isActive={isActive}
          isEven={isEven}
          onSelect={onSelectField}
        />
      );
    default:
      assertNever(field);
  }
}
